<template>
    <transition name="login-dialog-fade">
        <div v-if="value" class="login-dialog-mask" role="presentation">
            <section class="login-dialog" role="dialog" aria-modal="true" :aria-label="title">
                <div class="login-dialog__visual">
                    <img :src="loginVisual" alt="Vehicle history service illustration">
                </div>

                <div class="login-dialog__content">
                    <button class="login-dialog__close" type="button" aria-label="Close" @click="close">
                        <i class="el-icon-close"></i>
                    </button>

                    <div class="login-form">
                        <h2 class="login-form__title">{{ title }}</h2>

                        <form class="login-form__fields" novalidate @submit.prevent="submit">
                            <div class="login-field" :class="{ 'is-invalid': errors.email }">
                                <div class="login-field__control">
                                    <img :src="emailIcon" alt="">
                                    <span class="login-field__label">Email</span>
                                    <span class="login-field__divider"></span>
                                    <input
                                        v-model.trim="form.email"
                                        type="email"
                                        autocomplete="email"
                                        placeholder="Please enter your email address."
                                        @blur="validateEmail"
                                    >
                                </div>
                                <p v-if="errors.email" class="login-field__error">{{ errors.email }}</p>
                            </div>

                            <div v-if="needsCode" class="login-field" :class="{ 'is-invalid': errors.code }">
                                <div class="login-field__control">
                                    <img :src="verificationIcon" alt="">
                                    <span class="login-field__label">Captcha</span>
                                    <span class="login-field__divider"></span>
                                    <input
                                        v-model.trim="form.code"
                                        type="text"
                                        autocomplete="one-time-code"
                                        placeholder="Enter your verification code."
                                        @blur="validateCode"
                                    >
                                    <button
                                        v-if="!countdown"
                                        class="login-field__code-button"
                                        type="button"
                                        :disabled="sending"
                                        @click.stop="sendVerificationCode"
                                    >
                                        <i v-if="sending" class="el-icon-loading"></i>
                                        {{ sending ? 'Sending...' : 'Send Verification Code' }}
                                    </button>
                                    <span v-else class="login-field__countdown">Request another code in ({{ countdown }}s)</span>
                                </div>
                                <p v-if="errors.code" class="login-field__error">{{ errors.code }}</p>
                            </div>

                            <div v-if="needsPassword" class="login-field" :class="{ 'is-invalid': errors.password }">
                                <div class="login-field__control">
                                    <img :src="passwordIcon" alt="">
                                    <span class="login-field__label">Password</span>
                                    <span class="login-field__divider"></span>
                                    <input
                                        v-model="form.password"
                                        type="password"
                                        :autocomplete="mode === 'password' ? 'current-password' : 'new-password'"
                                        placeholder="8-20 letters + numbers, symbols allowed"
                                        @blur="validatePassword"
                                    >
                                </div>
                                <p v-if="errors.password" class="login-field__error">{{ errors.password }}</p>
                            </div>

                            <div v-if="needsConfirmPassword" class="login-field" :class="{ 'is-invalid': errors.confirmPassword }">
                                <div class="login-field__control">
                                    <img :src="passwordIcon" alt="">
                                    <span class="login-field__label">Confirm password</span>
                                    <span class="login-field__divider"></span>
                                    <input
                                        v-model="form.confirmPassword"
                                        type="password"
                                        autocomplete="new-password"
                                        placeholder="Please re-enter your password."
                                        @blur="validateConfirmPassword"
                                    >
                                </div>
                                <p v-if="errors.confirmPassword" class="login-field__error">{{ errors.confirmPassword }}</p>
                            </div>

                            <div v-if="mode === 'password'" class="login-form__assist">
                                <span>No account yet? <button type="button" @click="switchMode('register')">Sign up now</button></span>
                                <button type="button" @click="switchMode('reset')">Forgot password <span aria-hidden="true">→</span></button>
                            </div>

                            <div v-if="mode === 'code'" class="login-form__single-assist">
                                No account yet? <button type="button" @click="switchMode('register')">Sign up now</button>
                            </div>

                            <button class="login-form__submit" type="submit">{{ submitText }}</button>

                            <button
                                v-if="mode === 'code' || mode === 'password'"
                                class="login-form__switch"
                                type="button"
                                @click="switchLoginMethod"
                            >
                                <i class="el-icon-sort"></i>
                                {{ mode === 'code' ? 'Account password login' : 'Verification code login' }}
                            </button>

                            <p v-if="mode === 'register'" class="login-form__bottom-text">
                                Already have an account? <button type="button" @click="switchMode('password')">Sign In</button>
                            </p>
                        </form>
                    </div>
                </div>
            </section>
        </div>
    </transition>
</template>

<script>
import loginVisual from '@/assets/images/login-left-bg.png';
import emailIcon from '@/assets/images/icon/email-account-icon.png';
import verificationIcon from '@/assets/images/icon/verification-icon.png';
import passwordIcon from '@/assets/images/icon/password-icon.png';
import { login, sendEmailCode, emailLogin, addUser, editUser, getUserInfo } from '@/api/login';
import store from "@/store"

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const passwordPattern = /^(?=.*[A-Za-z])(?=.*\d)[^\s]{8,20}$/;

export default {
    name: 'LoginDialog',
    props: {
        value: {
            type: Boolean,
            default: false
        },
        initialMode: {
            type: String,
            default: 'code',
            validator: value => ['code', 'password', 'register', 'reset'].includes(value)
        }
    },
    data() {
        return {
            loginVisual,
            emailIcon,
            verificationIcon,
            passwordIcon,
            mode: 'code',
            countdown: 0,
            timer: null,
            sending: false,
            form: {
                email: '',
                code: '',
                password: '',
                confirmPassword: ''
            },
            errors: {
                email: '',
                code: '',
                password: '',
                confirmPassword: ''
            }
        };
    },
    computed: {
        title() {
            if (this.mode === 'register') return 'Sign up';
            if (this.mode === 'reset') return 'Reset password';
            return 'Sign in';
        },
        needsCode() {
            return this.mode === 'code' || this.mode === 'register' || this.mode === 'reset';
        },
        needsPassword() {
            return this.mode !== 'code';
        },
        needsConfirmPassword() {
            return this.mode === 'register' || this.mode === 'reset';
        },
        submitText() {
            if (this.mode === 'register') return 'Sign up';
            if (this.mode === 'reset') return 'Reset password';
            return 'Sign in';
        }
    },
    watch: {
        value(isVisible) {
            if (isVisible) {
                this.resetForm();
            }
        },
        initialMode(mode) {
            if (this.value) {
                this.mode = mode;
                this.clearErrors();
            }
        }
    },
    beforeDestroy() {
        this.clearTimer();
    },
    methods: {
        resetForm() {
            this.mode = this.initialMode;
            this.sending = false;
            this.form = {
                email: '',
                code: '',
                password: '',
                confirmPassword: ''
            };
            this.clearErrors();
            this.clearTimer();
        },
        close() {
            this.$emit('input', false);
            this.$emit('close');
        },
        switchMode(mode) {
            this.sending = false;
            this.form = {
                email: '',
                code: '',
                password: '',
                confirmPassword: ''
            };
            this.mode = mode;
            this.clearErrors();
            this.clearTimer();
        },
        switchLoginMethod() {
            this.switchMode(this.mode === 'code' ? 'password' : 'code');
        },
        clearErrors() {
            this.errors = {
                email: '',
                code: '',
                password: '',
                confirmPassword: ''
            };
        },
        validateEmail() {
            if (!this.form.email) {
                this.errors.email = 'Please enter your email address.';
            } else if (this.form.email.length > 30) {
                this.errors.email = 'Email address cannot exceed 30 characters.';
            } else if (!emailPattern.test(this.form.email)) {
                this.errors.email = 'Please enter a valid email address.';
            } else {
                this.errors.email = '';
            }
            return !this.errors.email;
        },
        validateCode() {
            this.errors.code = this.form.code ? '' : 'Please enter the verification code.';
            return !this.errors.code;
        },
        validatePassword() {
            if (!this.form.password) {
                this.errors.password = 'Please enter your password.';
            } else if ((this.mode === 'register' || this.mode === 'reset') && !passwordPattern.test(this.form.password)) {
                this.errors.password = 'Use 8-20 characters with at least one letter and one number.';
            } else {
                this.errors.password = '';
            }
            return !this.errors.password;
        },
        validateConfirmPassword() {
            if (!this.form.confirmPassword) {
                this.errors.confirmPassword = 'Please confirm your password.';
            } else if (this.form.confirmPassword !== this.form.password) {
                this.errors.confirmPassword = 'The passwords do not match.';
            } else {
                this.errors.confirmPassword = '';
            }
            return !this.errors.confirmPassword;
        },
        sendVerificationCode() {
            if (this.sending || this.countdown || !this.validateEmail()) return;
            this.sending = true;
            let scene = ""
            switch(this.mode) {
                case 'code': scene = "login"; break;
                case 'register': scene = "register"; break;
                case 'reset': scene = "resetPwd"; break;
            }
            sendEmailCode({
                username: this.form.email,
                scene
            }).then((res) => {
                if (res.code == 200) {
                    this.$message({
                        message: "sent successfully",
                        type: "success"
                    })
                    this.startCountDown()
                }
            }).catch(() => {
            }).finally(() => {
                this.sending = false;
            })
        },
        startCountDown() {
            this.clearTimer();
            this.$emit('send-code', { email: this.form.email, type: this.mode });
            this.countdown = 60;
            this.timer = window.setInterval(() => {
                this.countdown -= 1;
                if (this.countdown <= 0) {
                    this.clearTimer();
                }
            }, 1000);
        },
        clearTimer() {
            if (this.timer) {
                window.clearInterval(this.timer);
                this.timer = null;
            }
            this.countdown = 0;
        },
        submit() {
            const validations = [this.validateEmail()];
            if (this.needsCode) validations.push(this.validateCode());
            if (this.needsPassword) validations.push(this.validatePassword());
            if (this.needsConfirmPassword) validations.push(this.validateConfirmPassword());

            if (validations.every(Boolean)) {
                // 不同登录事件，不同处理
                switch(this.mode) {
                    case 'register': this.register(); break;
                    case 'reset': this.resetPassword(); break;
                    case 'code': this.codeLogin(); break;
                    case 'password': this.passwordLogin(); break;
                }
                // this.$emit('submit', {
                //     type: this.mode,
                //     email: this.form.email,
                //     code: this.form.code,
                //     password: this.form.password
                // });
            }
        },
        codeLogin() {
            let { email, code } = this.form
            emailLogin({
                username: email,
                emailCode: code
            }).then((res) => {
                if (res.code == 200) {
                    this.$message({
                        message: 'login successful',
                        type: 'success'
                    })
                    store.dispatch("auth/saveAuthentication", {
                        token: res.token
                    })
                    this.getUserInfo()
                }
            })
        },
        passwordLogin() {
            let { email, password } = this.form
            login({
                username: email,
                password
            }).then((res) => {
                if (res.code == 200) {
                    this.$message({
                        message: 'login successful',
                        type: 'success'
                    })
                    store.dispatch("auth/saveAuthentication", {
                        token: res.token
                    })
                    this.getUserInfo()
                }
            })
        },
        register() {
            let { email, password, code } = this.form
            addUser({
                username: email,
                password,
                emailCode: code
            }).then((res) => {
                if (res.code == 200) {
                    this.$message({
                        message: 'registered successfully',
                        type: 'success'
                    })
                    this.resetForm()
                }
            })
        },
        resetPassword() {
            let { email, password, code } = this.form
            editUser({
                username: email,
                password,
                emailCode: code
            }).then((res) => {
                if (res.code == 200) {
                    this.$message({
                        message: 'reset successful',
                        type: 'success'
                    })
                    this.resetForm()
                }
            })
        },
        getUserInfo() {
            getUserInfo().then((res) => {
                if (res.code == 200) {
                    store.dispatch("auth/saveUserInfo", res.user)
                }
            })
        }
    }
};
</script>

<style scoped lang="scss">
.login-dialog-mask {
    position: fixed;
    z-index: 2000;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: rgba(2, 7, 38, 0.45);
    height: 100vh;

    .login-dialog {
        display: flex;
        width: 1260px;
        height: 670px;
        overflow: hidden;
        background: linear-gradient(180deg, #f7f8fa 0%, #ffffff 100%);
        border-radius: 12px;
        box-shadow: 0 10px 30px rgba(2, 7, 38, 0.1);

        &__visual {
            flex: 0 0 542px;
            width: 542px;
            height: 670px;

            img {
                width: 100%;
                height: 100%;
                object-fit: cover;
            }
        }

        &__content {
            position: relative;
            display: flex;
            flex: 1;
            align-items: center;
            justify-content: center;
            min-width: 0;
        }

        &__close {
            position: absolute;
            top: 19px;
            right: 19px;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 30px;
            height: 30px;
            padding: 0;
            color: #003eb0;
            background: #fff;
            border: 1.07px solid #d2ddf0;
            border-radius: 50%;

            i {
                font-size: 16px;
                font-weight: 700;
            }
        }
    }
}

.login-form {
    width: 520px;
    margin: 0 98px;

    &__title {
        height: 50px;
        margin: 0 0 48px;
        color: #333;
        font-size: 36px;
        font-weight: 700;
        letter-spacing: -0.02em;
        line-height: 50px;
    }

    &__fields {
        display: flex;
        flex-direction: column;
        gap: 20px;
    }

    &__assist,
    &__single-assist,
    &__bottom-text {
        min-height: 24px;
        margin: 8px 0 82px;
        color: #333;
        font-size: 16px;
        font-weight: 500;
        letter-spacing: -0.02em;
        line-height: 24px;

        button {
            padding: 0;
            color: #1b6fe8;
            font-weight: inherit;
        }
    }

    &__assist {
        display: flex;
        justify-content: space-between;

        > button span {
            margin-left: 4px;
            font-size: 20px;
            line-height: 0;
            vertical-align: -1px;
        }
    }

    &__single-assist {
        margin-bottom: 82px;
    }

    &__submit,
    &__switch {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 66px;
        border-radius: 12px;
        letter-spacing: -0.02em;
    }

    &__submit {
        color: #333;
        background: #ffcd02;
        font-size: 20px;
        font-weight: 900;
    }

    &__switch {
        gap: 10px;
        margin-top: 0;
        color: #003eb0;
        background: #e5eeff;
        font-size: 20px;
        font-weight: 500;

        i {
            transform: rotate(90deg);
            font-size: 21px;
        }
    }

    &__bottom-text {
        margin: -2px 0 0;
        text-align: center;
    }
}

.login-field {
    &__control {
        display: flex;
        align-items: center;
        width: 100%;
        height: 62px;
        padding: 0 8px 0 20px;
        background: #f6f9ff;
        border: 2px solid #d2ddf0;
        border-radius: 12px;

        > img {
            flex: 0 0 20px;
            width: 20px;
            height: 22px;
            object-fit: contain;
        }

        input {
            flex: 1;
            min-width: 0;
            height: 100%;
            padding: 0 0 0 6px;
            color: #333;
            font-size: 14px;
            font-weight: 500;
            letter-spacing: -0.02em;

            &::placeholder {
                color: #9da7b8;
                opacity: 1;
            }
        }
    }

    &__label {
        flex: 0 0 auto;
        margin-left: 6px;
        color: #333;
        font-size: 16px;
        font-weight: 500;
        letter-spacing: -0.02em;
        white-space: nowrap;
    }

    &__divider {
        flex: 0 0 1px;
        width: 1px;
        height: 22px;
        margin-left: 6px;
        background: #e8eaf4;
    }

    &__code-button {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        flex: 0 0 171px;
        width: 171px;
        height: 46px;
        color: #003eb0;
        background: #e5eeff;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 500;
        letter-spacing: -0.02em;

        &:disabled {
            cursor: not-allowed;
            opacity: 0.65;
        }
    }

    &__countdown {
        flex: 0 0 auto;
        margin-left: 12px;
        color: #333;
        font-size: 14px;
        font-weight: 500;
        letter-spacing: -0.02em;
        white-space: nowrap;
    }

    &__error {
        min-height: 18px;
        margin: 6px 0 -4px;
        color: #f04438;
        font-size: 14px;
        font-weight: 500;
        line-height: 18px;
        text-align: center;
    }

    &.is-invalid {
        .login-field__control {
            border-color: #f04438;
        }
    }
}

.login-dialog-fade-enter-active,
.login-dialog-fade-leave-active {
    transition: opacity 0.2s ease;
}

.login-dialog-fade-enter-active .login-dialog,
.login-dialog-fade-leave-active .login-dialog {
    transition: transform 0.2s ease;
}

.login-dialog-fade-enter,
.login-dialog-fade-leave-to {
    opacity: 0;

    .login-dialog {
        transform: translateY(-8px);
    }
}
</style>
